/**
 * 배관 압력손실 검토 화면의 상태와 실행.
 *
 * 근거: API Standard 520 Part II, 6판(2015)
 *   입구 3 % 기준 7.3.4 · 손실에 포함할 것 7.3.7.2 · 유량 기준 7.3.7.3
 *   힘 평형 대안 7.3.6 d) · 배압 5.3 · 반력 식 (1)
 *
 * 유체역학은 표준 식이 아니라 Darcy–Weisbach + Colebrook–White 다
 * (표준이 특정 상관식을 지정하지 않는다).
 *
 * React 를 import 하지 않는다 — 화면 없이 시험할 수 있어야 한다.
 */
import { checkBackpressure, type BackpressureValveType } from "../calc/backpressure";
import { fittingTotals, type FittingItem, type FittingKind } from "../calc/fittings";
import {
  checkInletLoss,
  forceBalanceCheck,
  type InletFlowBasis,
  type InletLossExemption,
} from "../calc/inletLoss";
import {
  buildSegment,
  pressureDrop,
  PIPE_ROUGHNESS_M,
  type LossMethod,
} from "../calc/pipeFlow";
import { reactionForce } from "../calc/reactionForce";
import { innerDiameterMm } from "../data/pipes";
import { num } from "./formBits";
import {
  solveInletPressure,
  UNIVERSAL_GAS_CONSTANT,
} from "../calc/isothermalFlow";
import {
  ATMOSPHERIC_KPA,
  convertInput,
  toKpa,
  type PressureUnit,
} from "./pressureUnit";

export type PipeMaterial = keyof typeof PIPE_ROUGHNESS_M;

/**
 * 재질 표시 이름 — KOSHA GUIDE D-63-2018 <표2> 항목명을 그대로 쓴다.
 * 화면과 계산서가 같은 이름을 쓰도록 여기 한 곳에만 둔다.
 */
export const MATERIAL_LABELS: Record<PipeMaterial, string> = {
  commercialSteel: "탄소강 신관 (ε = 0.045 mm)",
  stainlessSteel: "스테인리스강 (ε = 0.03 mm)",
  drawnTubing: "인발관·유리·플라스틱 (ε = 0.0015 mm)",
  slightlyCorroded: "탄소강 경미 부식 (ε = 0.1 mm)",
  corrodedSteel: "탄소강 중간 부식 (ε = 0.5 mm)",
  badlyCorroded: "탄소강 심한 부식 (ε = 2.0 mm)",
};

/** 배관 한 구간의 화면 입력. 입구·출구가 같은 모양을 쓴다. */
export interface PipeRunState {
  /** 호칭경·스케줄로 내경을 정한다. 직접 입력하려면 useCustomId 를 켠다. */
  nps: string;
  schedule: string;
  useCustomId: boolean;
  customIdMm: string;
  material: PipeMaterial;
  /** 직관 길이 (m) */
  straightLengthM: string;
  fittings: FittingItem[];
}

export interface PipingState {
  /** 검토 대상 밸브 태그 — 계산서 표기용 */
  tag: string;
  /** 설정압 — 사용자 단위 게이지 */
  setPressure: string;
  /** MAWP — 사용자 단위 게이지 */
  mawp: string;
  /** 허용 축적 (%) */
  allowableAccumulationPercent: string;
  valveType: BackpressureValveType;

  /** 부속 손실 처리 방식 (등가길이 / 저항계수) */
  method: LossMethod;
  /**
   * 출구 배관을 등온 압축성으로 풀지 여부.
   *
   * KOSHA GUIDE D-63-2018 4.1(1) 은 **배출배관 전반**에 등온 과정을
   * 권한다. 출구 배관이 길거나 마하수가 높으면 비압축성 식이 크게
   * 어긋나므로 기본으로 켠다.
   *
   * ⚠ 입구 배관은 짧고 저마하수라 비압축성이 관행이며 API 520 Part II
   *   7.3 도 그쪽을 전제한다. 그래서 입구에는 적용하지 않는다.
   */
  outletCompressible: boolean;
  /** 출구 유체의 분자량 — 압축성 계산에 필요 */
  outletMolecularWeight: string;
  /** 출구 유체의 배출 온도 (K) */
  outletTemperatureK: string;
  /** 출구 유체의 압축계수 */
  outletCompressibility: string;
  /** 유량 기준 (7.3.7.3) */
  flowBasis: InletFlowBasis;
  /** 3 % 기준 예외 (7.3.4) */
  exemption: InletLossExemption;

  /** 유량 (kg/h) */
  flowKgH: string;
  /** 배출 조건 밀도 (kg/m³) */
  densityKgM3: string;
  /** 배출 조건 점도 (cP) — 화면은 cP, 계산은 Pa·s */
  viscosityCp: string;

  /** 상류 파열판 압력강하 — 사용자 단위 */
  ruptureDiskDrop: string;

  inlet: PipeRunState;
  outlet: PipeRunState;

  /** 중첩배압 — 사용자 단위 게이지 */
  superimposed: string;

  /** 힘 평형 판정 (3 % 초과 시) */
  blowdown: string;
  forceBalanceOverpressure: string;

  /** 반력 계산 */
  reaction: {
    enabled: boolean;
    specificHeatRatio: string;
    stagnationTemperatureK: string;
    molecularWeight: string;
    /** 방출점 출구 내경 (mm) — 면적으로 환산해 쓴다 */
    outletIdMm: string;
    /** 팽창 직전 배관 내부 정압 — 사용자 단위 게이지 */
    outletStaticPressure: string;
    usedRelievingTemperature: boolean;
  };
}

/** 단위 전환 때 함께 환산해야 하는 압력 입력. */
export const PIPING_PRESSURE_FIELDS = [
  "setPressure",
  "mawp",
  "ruptureDiskDrop",
  "superimposed",
  "blowdown",
  "forceBalanceOverpressure",
] as const satisfies readonly (keyof PipingState)[];

export function newPipeRun(nps: string, straightLengthM: string): PipeRunState {
  return {
    nps,
    schedule: "40",
    useCustomId: false,
    customIdMm: "",
    material: "commercialSteel",
    straightLengthM,
    fittings: [
      { kind: "entrance-sharp", count: 1 },
      { kind: "elbow90-long", count: 1 },
    ],
  };
}

export function newPipingState(): PipingState {
  return {
    tag: "PSV-101",
    setPressure: "517",
    mawp: "517",
    allowableAccumulationPercent: "10",
    valveType: "conventional",

    method: "equivalent-length",
    outletCompressible: true,
    outletMolecularWeight: "51",
    outletTemperatureK: "348",
    outletCompressibility: "0.9",
    flowBasis: "rated",
    exemption: "none",

    flowKgH: "24270",
    densityKgM3: "12",
    viscosityCp: "0.01",

    ruptureDiskDrop: "0",

    // API 520 Part I 예제(24,270 kg/h)의 선정 결과가 P 오리피스이고,
    // API 526 에서 P 는 6" x 8" 플랜지다. 배관을 밸브 플랜지보다 작게
    // 하지 않는다는 원칙(7.3.2)에 맞춰 기본값을 잡았다.
    inlet: newPipeRun("6", "3"),
    outlet: {
      ...newPipeRun("8", "30"),
      fittings: [
        { kind: "elbow90-long", count: 4 },
        { kind: "exit", count: 1 },
      ],
    },

    superimposed: "0",

    blowdown: "36",
    forceBalanceOverpressure: "52",

    reaction: {
      enabled: false,
      specificHeatRatio: "1.11",
      stagnationTemperatureK: "348",
      molecularWeight: "51",
      outletIdMm: "154",
      outletStaticPressure: "0",
      usedRelievingTemperature: false,
    },
  };
}

/** 단위를 바꿀 때 저장된 압력 입력을 새 단위로 환산한다. */
export function convertPipingPressures(
  s: PipingState,
  from: PressureUnit,
  to: PressureUnit,
): PipingState {
  if (from === to) return s;
  const next = { ...s };
  for (const key of PIPING_PRESSURE_FIELDS) {
    next[key] = convertInput(s[key], from, to);
  }
  next.reaction = {
    ...s.reaction,
    outletStaticPressure: convertInput(s.reaction.outletStaticPressure, from, to),
  };
  return next;
}

/** 화면이 고른 방식으로 내경(m)을 정한다. */
export function runInnerDiameterM(r: PipeRunState): number {
  if (r.useCustomId) return num(r.customIdMm) / 1000;
  const mm = innerDiameterMm(r.nps, r.schedule);
  return mm === null ? Number.NaN : mm / 1000;
}

/* ── 실행 ────────────────────────────────────────────────── */

export interface RunHydraulics {
  readonly innerDiameterMm: number;
  readonly equivalentLengthM: number;
  readonly fittingK: number;
  readonly totalLengthM: number;
  readonly velocityMs: number;
  readonly reynolds: number;
  readonly frictionFactor: number;
  readonly dropKpa: number;
  readonly frictionKpa: number;
  readonly fittingKpa: number;
  readonly flowRegime: "laminar" | "transitional" | "turbulent";
  readonly breakdown: ReturnType<typeof fittingTotals>["breakdown"];
  /** 등온 압축성으로 풀었으면 그 결과. 비압축성이면 null. */
  readonly compressible: {
    readonly outletMach: number;
    readonly inletMach: number;
    readonly criticalPressureKpaAbs: number;
    readonly outletPressureKpaAbs: number;
    readonly inletPressureKpaAbs: number;
    /**
     * 출구단(=관 끝, 압력이 가장 낮고 속도가 가장 빠른 지점) 밀도·속도.
     *
     * 압축성으로 풀면 배관을 따라 밀도가 변하므로, 입력란의 밀도
     * 한 값으로는 출구 배관을 표현할 수 없다. 그래서 이 두 값은
     * 분자량·온도·압축계수로 다시 계산한다.
     */
    readonly outletDensityKgM3: number;
    readonly outletVelocityMs: number;
  } | null;
  /** 등온 압축성으로 풀려다 실패한 사유. 성공했거나 비압축성이면 null. */
  readonly compressibleError: string | null;
}

export interface PipingOutcome {
  readonly ok: boolean;
  readonly message?: string;

  readonly inlet: RunHydraulics | null;
  readonly outlet: RunHydraulics | null;

  /** 입구 3 % 판정 */
  readonly inletCheck: ReturnType<typeof checkInletLoss> | null;
  /** 3 % 를 넘었을 때의 힘 평형 대안 판정 */
  readonly forceBalance: ReturnType<typeof forceBalanceCheck> | null;
  /** 배압 판정 */
  readonly backpressure: ReturnType<typeof checkBackpressure> | null;
  /** 반력 */
  readonly reaction: ReturnType<typeof reactionForce> | null;

  readonly warnings: readonly string[];
}

/** 등온 압축성으로 풀 때 필요한 배경값. */
interface CompressibleContext {
  readonly outletPressureKpaAbs: number;
  readonly molecularWeight: number;
  readonly temperatureK: number;
  readonly compressibility: number;
}

/** KOSHA GUIDE D-63-2018 5.2.6.2 — 배출배관 마하수 상한. */
export const KOSHA_MACH_MAX = 0.8;
/** 같은 조항 — 이 이하가 바람직하다. */
export const KOSHA_MACH_DESIRABLE = 0.6;

function hydraulicsOf(
  r: PipeRunState,
  massFlowKgS: number,
  densityKgM3: number,
  viscosityPaS: number,
  method: LossMethod,
  /** 주면 등온 압축성(KOSHA 4.1(1))으로 푼다. 없으면 비압축성. */
  compressibleCtx?: CompressibleContext,
): RunHydraulics | null {
  const dM = runInnerDiameterM(r);
  if (!Number.isFinite(dM) || dM <= 0) return null;

  const roughness = PIPE_ROUGHNESS_M[r.material];
  const totals = fittingTotals(r.fittings, dM);
  const seg = buildSegment(num(r.straightLengthM), dM, roughness, totals, method);
  const drop = pressureDrop(seg, massFlowKgS, densityKgM3, viscosityPaS);

  // KOSHA 4.1(1): 배출배관은 등온 과정으로 보는 것이 좋다.
  // 부속 K 는 등온식에 넣을 자리가 없으므로 등가길이로 환산해 더한다.
  let compressible: RunHydraulics["compressible"] = null;
  let compressibleError: string | null = null;
  let dropKpa = drop.pressureDropPa / 1000;
  let frictionKpa = drop.frictionPa / 1000;
  let fittingKpa = drop.fittingPa / 1000;
  if (compressibleCtx !== undefined) {
    const lengthWithK =
      seg.lengthM + (seg.fittingK * dM) / Math.max(drop.frictionFactor, 1e-9);
    const solved = solveInletPressure({
      massFlowKgH: massFlowKgS * 3600,
      outletPressureKpaAbs: compressibleCtx.outletPressureKpaAbs,
      innerDiameterM: dM,
      equivalentLengthM: lengthWithK,
      frictionFactor: drop.frictionFactor,
      compressibility: compressibleCtx.compressibility,
      temperatureK: compressibleCtx.temperatureK,
      molecularWeight: compressibleCtx.molecularWeight,
    });
    if (solved.ok) {
      dropKpa = solved.pressureDropKpa;
      // ρ = pM/(ZRT). p 는 kPa 이므로 1000 배, R 은 J/(kmol·K) 이므로
      // M 은 g/mol(=kg/kmol) 을 그대로 쓴다.
      const rhoOut =
        (compressibleCtx.outletPressureKpaAbs *
          1000 *
          compressibleCtx.molecularWeight) /
        (compressibleCtx.compressibility *
          UNIVERSAL_GAS_CONSTANT *
          compressibleCtx.temperatureK);
      const area = (Math.PI / 4) * dM * dM;
      compressible = {
        outletDensityKgM3: rhoOut,
        outletVelocityMs: massFlowKgS / (rhoOut * area),
        outletMach: solved.outletMach,
        inletMach: solved.inletMach,
        criticalPressureKpaAbs: solved.criticalPressureKpaAbs,
        outletPressureKpaAbs: compressibleCtx.outletPressureKpaAbs,
        inletPressureKpaAbs: solved.inletPressureKpaAbs,
      };
      // 등온식은 직관과 부속을 길이 하나로 합쳐 푼다 — 나눠 보여줄 때는
      // 등가길이 비율로 배분한다(저항 f·l/d 가 길이에 선형이므로).
      const fittingLen = lengthWithK - seg.lengthM;
      frictionKpa = lengthWithK > 0 ? (dropKpa * seg.lengthM) / lengthWithK : dropKpa;
      fittingKpa = lengthWithK > 0 ? (dropKpa * fittingLen) / lengthWithK : 0;
    } else {
      // 풀리지 않으면(초크 등) 비압축성 값을 그대로 두고 경고는 상위에서 낸다.
      compressibleError = solved.message;
    }
  }

  return {
    innerDiameterMm: dM * 1000,
    equivalentLengthM: totals.equivalentLengthM,
    fittingK: seg.fittingK,
    totalLengthM: seg.lengthM,
    // 압축성으로 풀었으면 입력 밀도 기준 속도는 뜻이 없다 — 출구단 값을 쓴다.
    velocityMs: compressible?.outletVelocityMs ?? drop.velocityMs,
    reynolds: drop.reynolds,
    frictionFactor: drop.frictionFactor,
    dropKpa,
    frictionKpa,
    fittingKpa,
    flowRegime: drop.flowRegime,
    breakdown: totals.breakdown,
    compressible,
    compressibleError,
  };
}

export function runPiping(s: PipingState, unit: PressureUnit): PipingOutcome {
  const empty = {
    inlet: null,
    outlet: null,
    inletCheck: null,
    forceBalance: null,
    backpressure: null,
    reaction: null,
    warnings: [] as string[],
  };

  const massFlowKgS = num(s.flowKgH) / 3600;
  const density = num(s.densityKgM3);
  const viscosityPaS = num(s.viscosityCp) / 1000; // cP → Pa·s

  for (const [label, v] of [
    ["유량", massFlowKgS],
    ["밀도", density],
    ["점도", viscosityPaS],
  ] as const) {
    if (!Number.isFinite(v) || v <= 0) {
      return { ok: false, message: `${label} 을(를) 확인하십시오.`, ...empty };
    }
  }

  const setKpa = toKpa(num(s.setPressure), unit);
  const warnings: string[] = [];

  const inlet = hydraulicsOf(s.inlet, massFlowKgS, density, viscosityPaS, s.method);
  // 출구 배관의 하류 압력 = 중첩배압 + 대기압. 여기서 거슬러 올라간다.
  const outletDownstreamKpaAbs =
    toKpa(num(s.superimposed), unit) + ATMOSPHERIC_KPA;
  const outlet = hydraulicsOf(
    s.outlet,
    massFlowKgS,
    density,
    viscosityPaS,
    s.method,
    s.outletCompressible
      ? {
          outletPressureKpaAbs: outletDownstreamKpaAbs,
          molecularWeight: num(s.outletMolecularWeight),
          temperatureK: num(s.outletTemperatureK),
          compressibility: num(s.outletCompressibility),
        }
      : undefined,
  );

  if (inlet === null) {
    return { ok: false, message: "입구 배관 내경을 확인하십시오.", ...empty };
  }
  if (outlet === null) {
    return { ok: false, message: "출구 배관 내경을 확인하십시오.", ...empty };
  }

  // 입구 3 % 판정 — 계산 계층이 파열판 몫까지 더해 준다.
  const inletDM = runInnerDiameterM(s.inlet);
  const inletTotals = fittingTotals(s.inlet.fittings, inletDM);
  const inletCheck = checkInletLoss({
    setPressureKpaGauge: setKpa,
    segment: buildSegment(
      num(s.inlet.straightLengthM),
      inletDM,
      PIPE_ROUGHNESS_M[s.inlet.material],
      inletTotals,
      s.method,
    ),
    massFlowKgS,
    densityKgM3: density,
    viscosityPaS,
    flowBasis: s.flowBasis,
    exemption: s.exemption,
    ruptureDiskDropKpa: toKpa(num(s.ruptureDiskDrop), unit),
  });

  // 출구 압력손실이 곧 축적배압이다.
  const builtUpKpa = outlet.dropKpa;
  const backpressure = checkBackpressure({
    setPressureKpaGauge: setKpa,
    mawpKpaGauge: toKpa(num(s.mawp), unit),
    allowableAccumulationPercent: num(s.allowableAccumulationPercent),
    superimposedKpaGauge: toKpa(num(s.superimposed), unit),
    builtUpKpaGauge: builtUpKpa,
    valveType: s.valveType,
    thermalReliefOnly: s.exemption === "thermal-relief",
  });

  // 3 % 를 넘었을 때만 힘 평형 대안을 계산한다 (7.3.6 d).
  let forceBalance: ReturnType<typeof forceBalanceCheck> | null = null;
  if (inletCheck.ok && !inletCheck.withinLimit) {
    forceBalance = forceBalanceCheck({
      inletLossKpa: inletCheck.totalLossKpa,
      builtUpBackpressureKpa: builtUpKpa,
      overpressureKpa: toKpa(num(s.forceBalanceOverpressure), unit),
      blowdownKpa: toKpa(num(s.blowdown), unit),
      valveType:
        s.valveType === "balanced-bellows" ? "balanced-bellows" : "conventional",
    });
  }

  let reaction: ReturnType<typeof reactionForce> | null = null;
  if (s.reaction.enabled) {
    const d = num(s.reaction.outletIdMm);
    reaction = reactionForce({
      massFlowKgS,
      specificHeatRatio: num(s.reaction.specificHeatRatio),
      stagnationTemperatureK: num(s.reaction.stagnationTemperatureK),
      molecularWeight: num(s.reaction.molecularWeight),
      outletAreaMm2: (Math.PI / 4) * d * d,
      outletStaticPressureKpaGauge: toKpa(
        num(s.reaction.outletStaticPressure),
        unit,
      ),
      usedRelievingTemperature: s.reaction.usedRelievingTemperature,
    });
  }

  // 입구 손실은 정상적으로 아주 작다. 출구보다 크면 관경 선정이 의심스럽다.
  if (inlet.dropKpa > outlet.dropKpa && outlet.dropKpa > 0) {
    warnings.push(
      "입구 압력손실이 출구보다 큽니다. 입구 배관이 밸브 입구 호칭경보다 " +
        "작지 않은지 확인하십시오 — 표준은 입구 배관을 밸브 입구보다 작게 " +
        "하지 말라고 합니다 (API 520 Part II 7.3.2).",
    );
  }
  // KOSHA 5.2.6.2: 배출배관 마하수는 0.8 을 넘지 말고, 0.6 이하가 바람직하다.
  if (outlet.compressible !== null) {
    const ma = outlet.compressible.outletMach;
    if (ma > KOSHA_MACH_MAX) {
      warnings.push(
        `출구 배관 출구단 마하수가 ${ma.toFixed(2)} 로 0.8 을 넘습니다. ` +
          "관경을 키우십시오 (KOSHA GUIDE D-63-2018 5.2.6.2).",
      );
    } else if (ma > KOSHA_MACH_DESIRABLE) {
      warnings.push(
        `출구 배관 출구단 마하수가 ${ma.toFixed(2)} 입니다. 허용 범위이나 ` +
          "0.6 이하가 바람직합니다 (KOSHA GUIDE D-63-2018 5.2.6.2).",
      );
    }
  } else if (outlet.compressibleError !== null) {
    warnings.push(
      `출구 배관을 등온 압축성으로 풀지 못해 비압축성 값으로 표시했습니다 — ` +
        `${outlet.compressibleError} 관경을 키우거나 입력값을 확인하십시오.`,
    );
  }
  if (s.method === "resistance-coefficient") {
    warnings.push(
      "저항계수(K) 방식으로 계산했습니다. 등가길이 방식과 결과가 다를 수 " +
        "있으니, 계산서에 어느 방식을 썼는지 반드시 남기십시오.",
    );
  }

  return {
    ok: true,
    inlet,
    outlet,
    inletCheck,
    forceBalance,
    backpressure,
    reaction,
    warnings,
  };
}

/** 부속 목록에 하나 더한다. 같은 종류가 있으면 개수만 올린다. */
export function addFitting(
  fittings: readonly FittingItem[],
  kind: FittingKind,
): FittingItem[] {
  const found = fittings.find((f) => f.kind === kind);
  if (found) {
    return fittings.map((f) =>
      f.kind === kind ? { ...f, count: f.count + 1 } : f,
    );
  }
  return [...fittings, { kind, count: 1 }];
}

export function setFittingCount(
  fittings: readonly FittingItem[],
  kind: FittingKind,
  count: number,
): FittingItem[] {
  if (count <= 0) return fittings.filter((f) => f.kind !== kind);
  return fittings.map((f) => (f.kind === kind ? { ...f, count } : f));
}
